/** biome-ignore-all lint/suspicious/noExplicitAny: <explanation> */

import type React from "react";
import { Table, TableCard } from "@/components/application/table/table";
import type { ChartConfig, MetricKey, TimeSeriesDataPoint } from "./MetricsBarChartView";

interface MetricsTableViewProps<T extends TimeSeriesDataPoint> {
  data: T[];
  config: ChartConfig<T>;

  // Table-specific options
  stickyHeader?: boolean;
  stickyFirstColumn?: boolean;
  size?: "sm" | "md";

  // Table orientation
  orientation?: "row-wise" | "column-wise"; // row-wise: metrics as rows, dates as columns; column-wise: dates as rows, metrics as columns

  // First column label (defaults to "Date")
  firstColumnLabel?: string;
}

export const MetricsTableView = <T extends TimeSeriesDataPoint>({
  data,
  config,
  stickyHeader = true,
  stickyFirstColumn = true,
  size = "sm",
  orientation = "row-wise",
  firstColumnLabel = "Date",
}: MetricsTableViewProps<T>) => {
  // Early return if no data
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  // Early return if no metrics configured
  if (!config.metrics || config.metrics.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No metrics selected</p>
      </div>
    );
  }

  // Get formatters with defaults
  const getFormatter = (metric: MetricKey<T>) => {
    return config.formatters[metric] || ((v: number) => v.toString());
  };

  const getLabel = (metric: MetricKey<T>) => {
    return config.labels[metric] || (metric as string);
  };

  // Format value helper
  const formatValue = (value: any, metric: MetricKey<T>) => {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return "-";
    }
    const formatter = getFormatter(metric);
    return formatter(Number(value));
  };

  // Render row-wise: metrics as rows, dates as columns
  if (orientation === "row-wise") {
    return (
      <TableCard.Root className="bg-secondary_alt flex flex-col flex-1 min-h-0" size={size} rounded={false}>
        <Table aria-label="Metrics Report" selectionMode="none" className="table-auto w-full">
          <Table.Header className={stickyHeader ? "sticky top-0 z-10 bg-secondary" : ""}>
            <Table.Row className="hover:bg-transparent border-b">
              <Table.Head
                id="metric"
                label="Metric"
                className={`text-left px-4 min-w-[200px] w-[200px] border-r border-gray-200 ${
                  stickyFirstColumn ? "sticky left-0 bg-gray-50 z-10" : ""
                }`}
                isRowHeader
              />
              {data.map((dataPoint, index) => (
                <Table.Head
                  key={index}
                  id={`date-${index}`}
                  label={dataPoint.dateLabel}
                  className="px-4 min-w-[120px]"
                />
              ))}
            </Table.Row>
          </Table.Header>

          <Table.Body
            className="text-xs bg-secondary_alt"
            items={config.metrics.map((metric) => ({ id: metric as string, name: metric as string }))}
          >
            {(metricItem) => {
              const metric = metricItem.name as MetricKey<T>;
              const label = getLabel(metric);

              return (
                <Table.Row key={metric as string} className="bg-primary">
                  <Table.Cell
                    className={`px-4 border-r border-gray-200 ${stickyFirstColumn ? "sticky left-0 bg-white z-10" : ""}`}
                  >
                    <span className="text-sm font-medium text-gray-700 block" title={label}>
                      {label}
                    </span>
                  </Table.Cell>
                  {data.map((dataPoint, dateIndex) => (
                    <Table.Cell key={dateIndex} className="whitespace-nowrap text-sm text-primary font-medium px-4">
                      <span className="text-sm font-medium text-gray-700">
                        {formatValue(dataPoint[metric], metric)}
                      </span>
                    </Table.Cell>
                  ))}
                </Table.Row>
              );
            }}
          </Table.Body>
        </Table>
      </TableCard.Root>
    );
  }

  // Render column-wise: dates as rows, metrics as columns
  return (
    <TableCard.Root className="bg-secondary_alt flex flex-col flex-1 min-h-0" size={size} rounded={false}>
      <Table aria-label="Metrics Report" selectionMode="none" className="table-auto w-full">
        <Table.Header className={stickyHeader ? "sticky top-0 z-10 bg-secondary" : ""}>
          <Table.Row className="hover:bg-transparent border-b">
            <Table.Head
              id="date"
              label={firstColumnLabel}
              className={`text-left px-4 min-w-[150px] w-[150px] border-r border-gray-200 ${
                stickyFirstColumn ? "sticky left-0 bg-gray-50 z-10" : ""
              }`}
              isRowHeader
            />
            {config.metrics.map((metric) => (
              <Table.Head
                key={metric as string}
                id={metric as string}
                label={getLabel(metric)}
                className="px-4 min-w-[120px]"
              />
            ))}
          </Table.Row>
        </Table.Header>

        <Table.Body
          className="text-xs bg-secondary_alt"
          items={data.map((dataPoint, index) => ({
            id: `date-${index}`,
            dateLabel: dataPoint.dateLabel,
            data: dataPoint,
          }))}
        >
          {(item) => {
            return (
              <Table.Row key={item.id} className="bg-primary">
                <Table.Cell
                  className={`px-4 border-r border-gray-200 ${stickyFirstColumn ? "sticky left-0 bg-white z-10" : ""}`}
                >
                  <span className="text-sm font-medium text-gray-700 block" title={item.dateLabel}>
                    {item.dateLabel}
                  </span>
                </Table.Cell>
                {config.metrics.map((metric) => (
                  <Table.Cell
                    key={metric as string}
                    className="whitespace-nowrap text-sm text-primary font-medium px-4"
                  >
                    <span className="text-sm font-medium text-gray-700">{formatValue(item.data[metric], metric)}</span>
                  </Table.Cell>
                ))}
              </Table.Row>
            );
          }}
        </Table.Body>
      </Table>
    </TableCard.Root>
  );
};
